import { useState } from 'react'
import {
  Box,
  Button,
  DialogContentText,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  refusalDetail,
  usePolicyConfigurationLookups,
  usePolicyConfigurationPointSolutionAction,
  type PointSolutionGroup,
  type PolicyConfigurationDetail,
  type PolicyPointSolution,
  type PolicyPointSolutionAction,
} from './BenefitPolicyService'
import { NumberedSectionCard } from '~/shared/ui'
import { PointSolutionDialog } from './PointSolutionDialog'
import { RecordDialog } from './RecordDialog'

interface PointSolutionsSectionProps {
  policyId: number
  pointSolutions: PolicyConfigurationDetail['point_solutions']
  isActive: boolean
  readOnly: boolean
}

/** In the order the member's screen stacks them. Keyed by the payload's own group names. */
const GROUPS = [
  { key: 'firefly', label: 'From Firefly', value: 'Firefly' },
  { key: 'employer', label: 'From the employer', value: 'Employer' },
] as const satisfies readonly { key: string; label: string; value: PointSolutionGroup }[]

/** Which dialog is up, and on which row. An add carries the group it lands in. */
type Target =
  | { kind: 'add'; group: PointSolutionGroup }
  | { kind: 'edit'; group: PointSolutionGroup; pointSolution: PolicyPointSolution }
  | { kind: 'remove'; pointSolution: PolicyPointSolution }

const PointSolutionRow = ({
  pointSolution,
  readOnly,
  onEdit,
  onRemove,
}: {
  pointSolution: PolicyPointSolution
  readOnly: boolean
  onEdit: () => void
  onRemove: () => void
}) => (
  <Stack
    direction="row"
    spacing={1.5}
    sx={{
      alignItems: 'center',
      py: 1,
      borderBottom: 1,
      borderColor: 'divider',
      '&:last-of-type': { borderBottom: 0 },
    }}
    data-testid={`point-solution-${pointSolution.id}`}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {pointSolution.partner_name || <em>Unnamed partner</em>}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {pointSolution.point_solution_url ? (
          <Link
            href={pointSolution.point_solution_url}
            target="_blank"
            rel="noopener"
            underline="hover"
          >
            {pointSolution.point_solution_url}
          </Link>
        ) : (
          'No link'
        )}
      </Typography>
    </Box>
    {!readOnly && (
      <Stack direction="row" spacing={0.5} sx={{ flex: 'none' }}>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={onEdit}
            data-testid={`point-solution-${pointSolution.id}-edit`}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove">
          <IconButton
            size="small"
            onClick={onRemove}
            data-testid={`point-solution-${pointSolution.id}-remove`}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    )}
  </Stack>
)

/**
 * Section 6: the partnerships a member sees under Additional benefits.
 *
 * Attaching only — a partnership is a Firefly-wide agreement maintained in Django Admin and shared
 * across plans, so this picks from the existing ones rather than authoring another. What belongs to
 * this plan is which partners it offers, under which group, behind which link.
 */
export const PointSolutionsSection = ({
  policyId,
  pointSolutions,
  isActive,
  readOnly,
}: PointSolutionsSectionProps) => {
  const [target, setTarget] = useState<Target | null>(null)
  const lookups = usePolicyConfigurationLookups()
  const pointSolutionAction = usePolicyConfigurationPointSolutionAction(policyId)

  const refusal = pointSolutionAction.isError
    ? refusalDetail(pointSolutionAction.error, 'Could not update the point solutions.')
    : undefined

  // Reset first: a refusal answers one attempt, and carrying it into the next dialog reads as a
  // fresh failure before anything has been sent.
  const openDialog = (next: Target) => {
    pointSolutionAction.reset()
    setTarget(next)
  }

  // Closes only on success: a refusal leaves the dialog open with the server's reason on it.
  const act = (action: PolicyPointSolutionAction) =>
    pointSolutionAction.mutate(action, { onSuccess: () => setTarget(null) })

  return (
    <NumberedSectionCard
      index={6}
      title="Additional benefits"
      hint="The partners a member sees on the Additional benefits screen, grouped by who funds them."
      testId="section-point-solutions"
    >
      <Stack spacing={3}>
        {GROUPS.map(({ key, label, value }) => {
          const rows = pointSolutions[key]
          return (
            <Stack key={key} spacing={1} sx={{ alignItems: 'flex-start' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {label}
              </Typography>

              {rows.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No partners under {label.toLowerCase()} yet.
                </Typography>
              ) : (
                <Box sx={{ width: '100%' }}>
                  {rows.map(pointSolution => (
                    <PointSolutionRow
                      key={pointSolution.id}
                      pointSolution={pointSolution}
                      readOnly={readOnly}
                      onEdit={() => openDialog({ kind: 'edit', group: value, pointSolution })}
                      onRemove={() => openDialog({ kind: 'remove', pointSolution })}
                    />
                  ))}
                </Box>
              )}

              {!readOnly && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => openDialog({ kind: 'add', group: value })}
                  data-testid={`add-point-solution-${key}`}
                >
                  Add partner
                </Button>
              )}
            </Stack>
          )
        })}
      </Stack>

      {(target?.kind === 'add' || target?.kind === 'edit') && (
        <PointSolutionDialog
          pointSolution={target.kind === 'edit' ? target.pointSolution : undefined}
          defaultGroup={target.group}
          partnerships={lookups.data?.partnerships ?? []}
          groupOptions={lookups.data?.point_solution_types ?? []}
          isActive={isActive}
          saving={pointSolutionAction.isPending}
          refusal={refusal}
          onClose={() => setTarget(null)}
          onConfirm={act}
        />
      )}
      {target?.kind === 'remove' && (
        <RecordDialog
          title={`Remove “${target.pointSolution.partner_name ?? 'this partner'}”?`}
          confirmLabel="Remove"
          destructive
          busy={pointSolutionAction.isPending}
          notice={
            isActive
              ? 'This policy is live. This comes off the member’s Additional benefits screen as soon as you remove it.'
              : undefined
          }
          refusal={refusal}
          testId="remove-point-solution"
          maxWidth="xs"
          onClose={() => setTarget(null)}
          onConfirm={() => act({ action: 'remove', point_solution: target.pointSolution.id })}
        >
          <DialogContentText>
            The partnership itself is untouched — this only takes it off this policy.
          </DialogContentText>
        </RecordDialog>
      )}
    </NumberedSectionCard>
  )
}
